import { useEffect, useMemo, useState } from "react"

import { Col, Form, Row } from "react-bootstrap"
import { BehaviorSubject, concat, of } from "rxjs"
import { catchError, filter, map, switchMap  } from "rxjs/operators"

import handleAPIError from "../../../utils/handleAPIError"
import LoadingTableBodyWrapper from "../../LoadingTableBodyWrapper"
import LoadingTableWrapper from "../../LoadingTableWrapper"
import TablePagination from "../../TablePagination"
import { CompletedMatchingTest, CompletedTestsGridData, CompletedTestsGridOptions, GetCompletedTestsObservable } from "../../../api/Randoms/PoolSelectionActions"
import ApiResult from "../../../api/ApiResult"
import FormatDate from "../../../formatters/FormatDate"

const PageSize = 5
interface CompletedTestsProps {
  search: { field: string; text: string }
  testDetailID: number
  testTypeID: number
  poolSelectionID: number
  selectedTest?: CompletedMatchingTest
  showTitle?: boolean
  onSelectTest: (test: CompletedMatchingTest | undefined) => void
}

function CompletedTests({
  search,
  testDetailID,
  testTypeID,
  poolSelectionID,
  selectedTest = undefined,
  showTitle = true,
  onSelectTest,
}: CompletedTestsProps) {
  const [gridData, setGridData] = useState<CompletedTestsGridData>({ Records: [], Pages: 0, Total: 0 })
  const [loadingTests, setLoadingTests] = useState<boolean>(false)

  const page$ = useMemo(
    () =>
      new BehaviorSubject<CompletedTestsGridOptions>({
        search: search ,
        TestDetailID: testDetailID,
        TestTypeID: testTypeID,
        PoolSelectionID: poolSelectionID,
        PageNumber: 1,
        PageSize,
      }),
    [search, poolSelectionID, testTypeID, testDetailID]
  )

  useEffect(() => {
    const subscription = page$
      .pipe(
        filter((params) => params.search.text.length > 0),
        switchMap((params) =>
          concat(
            of({ loading: true }),
            GetCompletedTestsObservable(params).pipe(
              map((result) => ({ loading: false, result })),
              catchError((error) => of({ loading: false, result: undefined, error }))
            )
          )
        )
      )
      .subscribe(
        ({ loading, result, error }: ApiResult<CompletedTestsGridData>) => {
          setLoadingTests(loading)

          if (!loading) {
            setGridData({
              ...result,
              Records: result?.Records || [],
              Pages: result?.Pages ?? 0,
              Total: result?.Total ?? 0,
            })
            handleAPIError(error)
          }
        },
        (error) => handleAPIError(error, () => setLoadingTests(false))
      )
    return () => subscription.unsubscribe()
  }, [page$, search.text])

  const handleSelectTest = (testId: number | undefined) => {
    const selected = gridData.Records.find((t) => t.TestDetailID === testId)
    onSelectTest(selected?.TestDetailID === selectedTest?.TestDetailID ? undefined : selected)
  }

  const handleChangePage = (page: number) => {
    handleSelectTest(undefined)
    setLoadingTests(true)
    page$.next({ ...page$.value, search, PageNumber: page })
  }

  return (
    <>
      <Row>
        <Col>{showTitle && <h5 className="text-body">Completed {testTypeID === 1 ? "Drug" : "Alcohol"} Tests</h5>}</Col>
      </Row>
      <LoadingTableWrapper loading={loadingTests} variant="light" withHeader>
        <table className="table table-bordered">
          <thead className="thead-light">
            <tr>
              <th className="text-center col-1" aria-label="..." />
              <th className="text-nowrap">Reason</th>
              <th className="text-nowrap">SampleID</th>
              <th className="text-nowrap">Regulated</th>
              <th className="text-nowrap">Type</th>
              <th className="text-nowrap">Created</th>
              <th className="text-nowrap">Name</th>
              <th className="text-nowrap">ID</th>
              <th className="text-nowrap">Company</th>
              <th className="text-nowrap">Location</th>
            </tr>
          </thead>
          <tbody className="bg-light">
            <LoadingTableBodyWrapper
              loading={loadingTests}
              recordsCount={gridData.Records.length}
              columnsCount={10}
              noDataText="No Tests Found"
            >
              {gridData.Records.map((test: CompletedMatchingTest) => (
                <tr>
                  <td className="align-middle">
                    <Form.Check
                      type="checkbox"
                      name="pending-test-detail"
                      id={`ptd_${test.TestDetailID}`}
                      className="abc-checkbox abc-checkbox-primary d-flex justify-content-center align-middle"
                      value={test.TestDetailID}
                      label=""
                      disabled={!!selectedTest && selectedTest?.TestDetailID !== test.TestDetailID}
                      checked={selectedTest?.TestDetailID === test.TestDetailID}
                      readOnly
                      onChangeCapture={() => handleSelectTest(test.TestDetailID)}
                    />
                  </td>
                <td title={test.Reason}>{test.ReasonDescription}</td>
                <td>{test.SampleID}</td>
                <td>{test.Regulated}</td>
                <td>{test.SampleType}</td>
                <td>{FormatDate(test.DateOrdered)}</td>
                <td>{test.CCFLastName}, {test.CCFFirstName}</td>
                <td>{test.CCFID}</td>
                <td>{test.Company}</td>
                <td>{test.Location}</td>
              </tr>
              ))}
            </LoadingTableBodyWrapper>
          </tbody>
        </table>
      </LoadingTableWrapper>
      {gridData.Records.length > 0 && (
        <Row>
          <Col sm={2}>Total records: {gridData.Total}</Col>
          <Col>
            <TablePagination
              pageNumber={page$.value.PageNumber}
              pages={gridData.Pages}
              onChange={handleChangePage}
              className="mb-0"
            />
          </Col>
        </Row>
      )}
    </>
  )
}

export default CompletedTests
